import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import { devtools } from 'zustand/middleware';

import type {
  AccessExtended,
  AccessRoleEnum,
  MeWithAccessRecordsApiResponse,
  PreferencesRecord,
  ProfileRecord,
  UserCardValues,
  UserDocument,
  UserDocumentWithAccess,
} from '@youscience/bpa-common';

import { authService } from '@services/auth.api.service';
import { selectUserCard } from '@utils/dataCards';

import { BRIGHTPATH_HOME_URL, AUTHENTICATION_BASE_URL } from '@constants/externalRoutes';
import { conditionalRedirect } from '@utils/navigation';

import { CurrentAccess, ImpersonatedBy, SchoolOfRecord, UserRoles } from '@interfaces/user';

import { hasOnlyLearnerAccess, resolveDefaultAccess } from '@common-types';

// TODO - user-common-service needs to be updated to include the field gradeLevel
export interface ProfileGradeAndPlan {
  gradeLevel?: number;
}
export type ProfileWithExtendedData = ProfileRecord & ProfileGradeAndPlan;

export type AuthSession<Y = AccessRoleEnum, T = boolean> = {
  currentAccess?: CurrentAccess;
  impersonatedBy?: ImpersonatedBy;
  isAuthenticated: T;
  isImpersonated: boolean;
  schoolOfRecord?: SchoolOfRecord;
  userData?: UserDocumentWithAccess;
  userCard?: UserCardValues;
  userType?: Y;
};

export interface AuthSessionContext {
  authSession: AuthSession;
  initializedUser: boolean;
  getMe: () => Promise<MeWithAccessRecordsApiResponse | undefined>;
  resetAuthSession: () => void;
  setPreferences: (newPreference: string) => void;
  signIn: (param?: string, search?: string) => unknown;
  signOut: () => void;
}

const initialAuthSession: AuthSession = {
  currentAccess: undefined,
  impersonatedBy: undefined,
  isImpersonated: false,
  isAuthenticated: false,
  schoolOfRecord: undefined,
  userData: undefined,
  userCard: undefined,
  userType: undefined,
};

const initialState: AuthSessionContext = {
  authSession: { ...initialAuthSession },
  initializedUser: false,
  getMe: async () => undefined,
  resetAuthSession: () => undefined,
  setPreferences: () => undefined,
  signIn: () => undefined,
  signOut: () => undefined,
};

const getRole = (currentRole?: AccessRoleEnum): AccessRoleEnum => {
  const role = currentRole ?? UserRoles.Learner;
  if (role === UserRoles.Alumni) {
    return UserRoles.Learner;
  }
  return role;
};

export { getRole };

const EMPTY_CURRENT_ACCESS: CurrentAccess = {
  accessDocumentId: '',
  role: UserRoles.Learner,
  tenantId: '',
  constraintTags: [],
  tenantName: '',
  userId: '',
  commonAppEnabled: false,
};

type SessionAccess = Pick<AccessExtended, '_id' | 'user'> & {
  tenant: Pick<AccessExtended['tenant'], 'tenantId' | 'name' | 'permission' | 'productAvailability'>;
};

const toCurrentAccess = (access: SessionAccess): CurrentAccess => ({
  accessDocumentId: access._id.toString(),
  constraintTags: access.user?.grants ?? [],
  role: getRole(access.tenant?.permission?.role),
  tenantId: access.tenant?.tenantId ?? '',
  tenantName: access.tenant?.name ?? '',
  userId: access.user?.userId ?? '',
  commonAppEnabled: access.tenant?.productAvailability?.commonApp ?? false,
});

export const useAuthStore = createWithEqualityFn<AuthSessionContext>()(
  devtools((set, get) => ({
    ...initialState,

    signIn: (pathname?: string, search?: string) => {
      conditionalRedirect(
        `${AUTHENTICATION_BASE_URL}?redirectUrl=${window.location.protocol}//${window.location.host}${pathname}${search}`,
        'replace',
      );
    },

    signOut: () => {
      conditionalRedirect(
        `${AUTHENTICATION_BASE_URL}/logout?logoutRedirectUrl=${window.location.protocol}//${window.location.host}`,
        'replace',
      );
    },

    resetAuthSession: () => {
      set((state) => ({ ...state, authSession: { ...initialAuthSession }, initializedUser: false }));
      conditionalRedirect(`${window.location.protocol}//${window.location.host}/`, 'assign');
    },

    setPreferences: (newPreference: string) => {
      const { authSession } = get();

      let currentAccess: CurrentAccess;
      let isAuthenticated: boolean;
      let newAuthSession: AuthSession;
      let newPreferredAccess: AccessExtended | undefined;

      if (authSession.userData?.access.length) {
        newPreferredAccess = authSession.userData.access.find(
          (access: AccessExtended) => access._id.toString() === newPreference,
        );
      }

      if (newPreferredAccess) {
        currentAccess = toCurrentAccess(newPreferredAccess);

        isAuthenticated = true;

        newAuthSession = {
          ...authSession,
          currentAccess,
          isAuthenticated,
          userType: currentAccess.role,
          userData: {
            ...authSession.userData,
            fullName: newPreferredAccess.user.fullName,
            userId: newPreferredAccess.user.userId,
          } as UserDocument,
        } as AuthSession;

        set(() => ({ authSession: { ...newAuthSession } }));
      }
    },

    getMe: async () => {
      set((state) => ({ ...state }));

      try {
        const userData = (await authService.getUser()) as MeWithAccessRecordsApiResponse;
        const receivedPreference = (await authService.getUserPreferences()) as PreferencesRecord;

        if (userData) {
          const defaultAccess = resolveDefaultAccess(userData);
          const isLearnerOnly = hasOnlyLearnerAccess(userData.access ?? []);
          const currentAccess = defaultAccess ? toCurrentAccess(defaultAccess) : EMPTY_CURRENT_ACCESS;

          if (!defaultAccess) {
            conditionalRedirect(BRIGHTPATH_HOME_URL, 'replace');
          }

          set(() => ({
            authSession: {
              currentAccess,
              impersonatedBy: userData.impersonatedBy ?? undefined,
              isAuthenticated: true,
              isImpersonated: !!userData.impersonatedBy,
              schoolOfRecord: userData.schoolOfRecord ?? undefined,
              userData,
              userCard: selectUserCard(userData.cards),
              userType: currentAccess.role,
            } as AuthSession,
          }));

          const storedAccessId = receivedPreference?.currentAccess?.toString();

          if (defaultAccess && isLearnerOnly) {
            if (storedAccessId !== defaultAccess._id.toString()) {
              void authService.updateUserPreferences({ currentAccess: defaultAccess._id }).catch(() => undefined);
            }
          } else if (defaultAccess && storedAccessId && storedAccessId !== defaultAccess._id.toString()) {
            get().setPreferences(storedAccessId);
          }

          return userData;
        }
      } catch {
        set(() => ({
          authSession: {
            ...initialAuthSession,
            isAuthenticated: false,
          },
        }));
      } finally {
        set({ initializedUser: true });
      }
      return undefined;
    },
  })),
  shallow,
);

export const signOutCallback = (): (() => void) => {
  const closure = useAuthStore.getState().signOut;

  return closure;
};

export const getMeCallback: () => Promise<MeWithAccessRecordsApiResponse | undefined> = () => {
  const closure = useAuthStore.getState().getMe;

  return closure();
};

export const setPreferences: (preferredAccess: string) => void = (preferredAccess: string) => {
  const closure = useAuthStore.getState().setPreferences;

  return closure(preferredAccess);
};

export const getAuthSession: () => AuthSession | undefined = (): AuthSession | undefined => {
  const { authSession } = useAuthStore.getState();

  return authSession;
};

export const getCurrentAccess: () => CurrentAccess | undefined = () => {
  const closure = useAuthStore.getState().authSession.currentAccess;

  return closure;
};

export const resetAuthSession: () => void = () => {
  const closure = useAuthStore.getState().resetAuthSession;

  return closure();
};
